import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { ContactValidationResult, FieldsStatus } from 'api/file/interfaces/get-file-order-insights';
import { useMemo } from 'react';
import { formatDate } from 'utilities/dateFormatter';
import { formatFullAddress, formatPhoneNumber, isEmailOlderThanSixMonths } from 'utilities/utilities';
import OrderInsightAlert from './OrderInsightAlerts';
import StatusRow from './StatusRow';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  statusContainer: {
    marginBottom: theme.spacing(2),
  },

  heading: {
    paddingLeft: theme.spacing(2),
    fontWeight: 600,
  },

  pdfHeader: {
    letterSpacing: pxToRem(0.5),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
}));

const ContactInfoDisplay: React.FC<ContactValidationResult> = ({
  isPhoneMatch,
  phoneCarrier,
  phoneType,
  isPrepaidPhone,
  emailRegisteredDate,
  risks = [],
  isPdf = false,
  phoneMatchesSubmittedData,
  prepaidPhone,
  streetAddressMatch,
  submittedData,
}: ContactValidationResult) => {
  const classes = useStyles();
  const { phoneNumber, emailAddress, address, city, stateCode, postalCode } = submittedData ?? {};

  const formatMode = (value: string, valueToCompare?: string) => {
    // value will be undefined if api gives us undefined data in case of API failure
    // refer to ANS-3260
    if (valueToCompare === undefined || value === undefined || value === FieldsStatus.NOT_APPLICABLE) {
      return FieldsStatus.DEFAULT;
    }
    if (value?.toLowerCase() === valueToCompare?.toLowerCase()) {
      return FieldsStatus.PASSED;
    }
    return FieldsStatus.FAILED;
  };

  const phoneValidationInfo = [
    { label: 'Phone Number', value: phoneNumber ? formatPhoneNumber(phoneNumber) : '-', isPlainText: true },
    {
      label: 'Matches Submitted Data?',
      value: phoneNumber ? phoneMatchesSubmittedData : '-',
      mode: formatMode(phoneMatchesSubmittedData, FieldsStatus.YES),
    },
    {
      label: 'Phone Carrier:',
      value: phoneCarrier,
      isPlainText: phoneCarrier !== FieldsStatus.NOT_APPLICABLE,
      mode: formatMode(phoneCarrier),
    },
    { label: 'Phone Type:', value: phoneType, mode: formatMode(phoneType, 'WIRELESS') },
    {
      label: 'Is Phone Prepaid?:',
      value: phoneNumber ? prepaidPhone : '-',
      mode: formatMode(prepaidPhone, FieldsStatus.NO),
    },
  ];

  const emailInfo = useMemo(() => {
    if (!emailAddress) {
      return [
        { label: 'Email Address:', value: '-', isPlainText: true },
        { label: 'First Identified in Public Record:', value: '-', isPlainText: true },
      ];
    }
    const isEmailOld = isEmailOlderThanSixMonths(emailRegisteredDate);
    const isNotApplicable = emailRegisteredDate === FieldsStatus.NOT_APPLICABLE;
    const emailMode = isEmailOld ? FieldsStatus.PASSED : FieldsStatus.FAILED;
    return [
      { label: 'Email Address:', value: emailAddress, isPlainText: true },
      {
        label: 'First Identified in Public Record:',
        value: isNotApplicable ? FieldsStatus.NOT_APPLICABLE : formatDate(emailRegisteredDate, 'MM/dd/yyyy', 'short'),
        mode: isNotApplicable ? FieldsStatus.DEFAULT : emailMode,
      },
    ];
  }, [emailRegisteredDate, emailAddress]);

  const streetInfo = useMemo(() => {
    const formatAddress = formatFullAddress(address, city, stateCode, postalCode);
    const isAddressMissing = formatAddress === '-';

    return [
      { label: 'Street Address:', value: formatAddress, isPlainText: true },
      {
        label: 'Matches Submitted Data?',
        value: isAddressMissing ? '-' : streetAddressMatch,
        ...(isAddressMissing ? { isPlainText: true } : { mode: formatMode(streetAddressMatch, FieldsStatus.YES) }),
      },
    ];
  }, [address, city, stateCode, postalCode, streetAddressMatch]);

  return (
    <Box>
      <div className={classes.statusContainer}>
        <Typography variant="h4" className={`${classes.heading} ${isPdf ? classes.pdfHeader : ''}`}>
          Phone Validation
        </Typography>
        <Box>
          {phoneValidationInfo.map((item, idx) => (
            <StatusRow
              key={`${item.label}-${idx}`}
              index={idx}
              label={item.label}
              value={phoneNumber ? item.value : '-'}
              mode={item.mode}
              isPlainText={phoneNumber ? item.isPlainText : true}
              interpretBooleanAsText
              isPdf={isPdf}
            />
          ))}
        </Box>
      </div>
      <div className={classes.statusContainer}>
        <Typography variant="h4" className={`${classes.heading} ${isPdf ? classes.pdfHeader : ''}`}>
          Email Validation
        </Typography>
        <Box>
          {emailInfo.map((item, idx) => (
            <StatusRow
              key={`${item.label}-${idx}`}
              index={idx}
              label={item.label}
              value={item.value}
              mode={item.mode}
              isPlainText={item.isPlainText}
              interpretBooleanAsText
              isPdf={isPdf}
            />
          ))}
        </Box>
      </div>
      <Typography variant="h4" className={`${classes.heading} ${isPdf ? classes.pdfHeader : ''}`}>
        Street Address Validation
      </Typography>
      <Box>
        {streetInfo.map((item, idx) => (
          <StatusRow
            key={`${item.label}-${idx}`}
            index={idx}
            label={item.label}
            value={item.value}
            {...('mode' in item && { mode: item.mode })}
            isPlainText={item.isPlainText}
            interpretBooleanAsText
            isPdf={isPdf}
          />
        ))}
      </Box>
      <OrderInsightAlert errorMessages={risks} isPdf={isPdf} />
    </Box>
  );
};

export default ContactInfoDisplay;
